/**
 * O que faz: carrega os responsáveis ativos da clínica para atribuição de retornos programados.
 * Onde usa: formulários e filtros da feature de retornos.
 * Conexões: useClinica, React Query e Supabase client com RLS.
 * Tabelas: user_profiles.
 * Data: 24/08/2026
 */

import { useQuery } from '@tanstack/react-query';
import { useClinica } from '@/contexts/ClinicaContext';
import { supabase } from '@/integrations/supabase/client';
import type { ReturnAssignee } from './returnTypes';

interface ReturnAssigneeRow {
  id: string;
  nome_completo: string | null;
  user_id: string;
}

export const useReturnAssignees = (targetClinicaId?: string | null) => {
  const { clinicaAtiva } = useClinica();
  // Atribuições no chat seguem a clínica do lead; null não deve reutilizar a clínica do perfil.
  const clinicaId = targetClinicaId === undefined ? clinicaAtiva?.id : targetClinicaId;

  return useQuery({
    queryKey: clinicaId
      ? ['lead-returns', 'assignees', clinicaId] as const
      : ['lead-returns', 'assignees', 'disabled'] as const,
    enabled: Boolean(clinicaId),
    queryFn: async (): Promise<ReturnAssignee[]> => {
      if (!clinicaId) {
        return [];
      }

      // Tabela: user_profiles | Operação: SELECT.
      // Campos: somente id, nome_completo e user_id para povoar o seletor de responsável.
      // Filtros: clinica_id ativo e status_usuario = ativo.
      // Risco: nome do usuário é dado pessoal; a RLS continua sendo a barreira principal no servidor.
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, nome_completo, user_id')
        .eq('clinica_id', clinicaId)
        .eq('status_usuario', 'ativo')
        .order('nome_completo', { ascending: true, nullsFirst: false });

      if (error) {
        throw new Error('Não foi possível carregar os responsáveis da clínica.');
      }

      return (data as ReturnAssigneeRow[] | null) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
};
